/**
 * Settings — device info, notification toggle, hard-reset, forget-device,
 * + theme toggle (premium polish pass).
 *
 * Sections are now Card-styled with lucide icons. Destructive actions
 * have explicit confirm modals. Sync-queue snapshot is mounted as a
 * read-only section so techs can see how many records are pending.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  Lock,
  Fingerprint,
  AlertCircle,
  Bell,
  RefreshCw,
  Smartphone,
  Cloud,
  Sun,
  Moon,
} from '../components/icons';
import { getInitialTheme, setTheme as persistTheme } from '../lib/theme';
import clsx from 'clsx';
import { getDb, wipeDb } from '../db';
import { useSession } from '../hooks/useSession';
import { setSession } from '../auth/session';
import {
  disablePush,
  enablePush,
  getPushStatus,
  type PushStatus,
} from '../push/subscribe';
import { getEngineSnapshot, kickEngine, subscribeEngine } from '../sync/engine';
import { BTN_SECONDARY_CLS, BTN_DANGER_CLS, CARD_CLS, LABEL_CLS } from '../styles/tokens';
import * as haptics from '../lib/haptics';

interface DeviceInfo {
  paired_at: string;
  unlock_method: 'webauthn' | 'pin';
}

export function SettingsPage() {
  const navigate = useNavigate();
  const session = useSession();
  const [device, setDevice] = useState<DeviceInfo | null>(null);
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [engine, setEngine] = useState(getEngineSnapshot);
  const [theme, setTheme] = useState<'dark' | 'light'>(getInitialTheme);

  useEffect(() => subscribeEngine(setEngine), []);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const dev = await getDb().device.get('self');
      if (cancelled) return;
      if (dev) {
        setDevice({
          paired_at: dev.paired_at,
          unlock_method: dev.unlock_method,
        });
      }
      setPushStatus(await getPushStatus());
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const togglePush = async () => {
    haptics.tap();
    setPushBusy(true);
    setPushError(null);
    try {
      if (pushStatus === 'subscribed') {
        await disablePush();
        setPushStatus(await getPushStatus());
      } else {
        const next = await enablePush();
        setPushStatus(next);
        if (next === 'denied') {
          setPushError(
            'Notifications are blocked. Enable them in your browser settings for this site.',
          );
        } else if (next === 'server-not-configured') {
          setPushError("Push notifications aren't configured on the server yet.");
        } else if (next === 'unsupported') {
          setPushError("This browser doesn't support push notifications.");
        }
      }
    } catch (err) {
      setPushError((err as Error).message);
    } finally {
      setPushBusy(false);
    }
  };

  const pushButtonLabel = useMemo(() => {
    if (pushBusy) return 'Working…';
    switch (pushStatus) {
      case 'subscribed':
        return 'Disable alerts';
      case 'denied':
        return 'Alerts blocked';
      case 'unsupported':
        return 'Alerts unavailable';
      case 'server-not-configured':
        return 'Server not configured';
      default:
        return 'Enable alerts';
    }
  }, [pushBusy, pushStatus]);
  const pushButtonDisabled =
    pushBusy ||
    pushStatus === 'denied' ||
    pushStatus === 'unsupported' ||
    pushStatus === 'server-not-configured';

  return (
    <div className="flex-1 flex flex-col p-6 gap-5">
      <Link
        to="/events"
        className="inline-flex items-center gap-1 text-mono-400 text-sm hover:text-mono-200 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Events
      </Link>

      <h1 className="text-2xl font-bold text-mono-100 tracking-tight">Settings</h1>

      {/* Account */}
      <section className={`${CARD_CLS} p-4 space-y-2`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30 flex items-center justify-center">
            <Smartphone className="h-4 w-4 text-cyan-300" strokeWidth={2} />
          </div>
          <span className={LABEL_CLS}>Account</span>
        </header>
        <div className="text-mono-100 font-medium truncate">{session?.user.email}</div>
        <div className="text-mono-400 text-xs">
          {session?.user.role} · {session?.user.client_id?.slice(0, 8)}
        </div>
        {device && (
          <div className="text-mono-400 text-xs flex items-center gap-1.5 pt-1 border-t border-mono-800/60">
            {device.unlock_method === 'webauthn'
              ? <><Fingerprint className="h-3.5 w-3.5" strokeWidth={2} /> biometric unlock</>
              : <><Lock className="h-3.5 w-3.5" strokeWidth={2} /> PIN unlock</>}
            <span className="text-mono-600">·</span>
            paired {new Date(device.paired_at).toLocaleDateString()}
          </div>
        )}
      </section>

      {/* Sync queue */}
      <section className={`${CARD_CLS} p-4 space-y-3`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30 flex items-center justify-center">
            <Cloud className="h-4 w-4 text-cyan-300" strokeWidth={2} />
          </div>
          <span className={LABEL_CLS}>Sync</span>
        </header>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Stat label="Queued" value={engine.queued} tone="cyan" />
          <Stat label="Failed" value={engine.failed} tone={engine.failed > 0 ? 'red' : 'mono'} />
          <Stat label="State" value={engine.state} tone="mono" />
        </div>
        <button
          type="button"
          className={`${BTN_SECONDARY_CLS} inline-flex items-center justify-center gap-1.5`}
          onClick={() => { haptics.tap(); void kickEngine(); }}
          disabled={engine.state === 'syncing'}
        >
          <RefreshCw className={clsx('h-4 w-4', engine.state === 'syncing' && 'animate-spin')} strokeWidth={2} />
          {engine.state === 'syncing' ? 'Syncing…' : 'Sync now'}
        </button>
      </section>

      {/* Notifications */}
      <section className={`${CARD_CLS} p-4 space-y-3`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30 flex items-center justify-center">
            <Bell className="h-4 w-4 text-cyan-300" strokeWidth={2} />
          </div>
          <span className={LABEL_CLS}>Notifications</span>
        </header>
        <p className="text-mono-300 text-sm">
          We'll only nudge you when you have unsynced field records or an
          event closing soon. No marketing, no noise.
        </p>
        <button
          type="button"
          className={BTN_SECONDARY_CLS}
          disabled={pushButtonDisabled}
          onClick={togglePush}
        >
          {pushButtonLabel}
        </button>
        {pushError && (
          <p className="text-accent-amber text-xs flex items-start gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={2} />
            <span>{pushError}</span>
          </p>
        )}
      </section>

      {/* Display — theme toggle. */}
      <section className={`${CARD_CLS} p-4 space-y-3`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-cyan-500/15 ring-1 ring-cyan-500/30 flex items-center justify-center">
            {theme === 'dark'
              ? <Moon className="h-4 w-4 text-cyan-300" strokeWidth={2} />
              : <Sun className="h-4 w-4 text-cyan-300" strokeWidth={2} />}
          </div>
          <span className={LABEL_CLS}>Display</span>
        </header>
        <p className="text-mono-300 text-sm">
          Dark mode is the default — easier on eyes in cabs and at night.
          Daytime techs can flip to light mode here.
        </p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => { haptics.tap(); setTheme('dark'); persistTheme('dark'); }}
            className={`flex-1 min-h-touch rounded-md border inline-flex items-center justify-center gap-1.5 text-sm transition-colors ${theme === 'dark' ? 'bg-cyan-500/15 text-cyan-200 border-cyan-500/40 ring-1 ring-cyan-500/30' : 'bg-mono-900 text-mono-200 border-mono-700 active:bg-mono-800'}`}
          >
            <Moon className="h-4 w-4" strokeWidth={2} /> Dark
          </button>
          <button
            type="button"
            onClick={() => { haptics.tap(); setTheme('light'); persistTheme('light'); }}
            className={`flex-1 min-h-touch rounded-md border inline-flex items-center justify-center gap-1.5 text-sm transition-colors ${theme === 'light' ? 'bg-cyan-500/15 text-cyan-200 border-cyan-500/40 ring-1 ring-cyan-500/30' : 'bg-mono-900 text-mono-200 border-mono-700 active:bg-mono-800'}`}
          >
            <Sun className="h-4 w-4" strokeWidth={2} /> Light
          </button>
        </div>
      </section>

      {/* Hard reset */}
      <section className={`${CARD_CLS} p-4 space-y-3`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-amber-500/15 ring-1 ring-amber-500/30 flex items-center justify-center">
            <RefreshCw className="h-4 w-4 text-amber-300" strokeWidth={2} />
          </div>
          <span className={LABEL_CLS}>App</span>
        </header>
        <p className="text-mono-300 text-sm">
          Hard reset — clears the cached app, all local data, and the
          service worker, then reloads on the latest version. Use this if
          something looks stale and a normal reload doesn't fix it. Re-pair
          required afterward, so sync anything pending first.
        </p>
        <button
          type="button"
          className={BTN_SECONDARY_CLS}
          onClick={() => {
            if (
              confirm(
                'Hard reset? All local data + unsynced drafts are wiped and you must re-pair.',
              )
            ) {
              window.location.href = '/reset.html';
            }
          }}
        >
          Hard reset (force latest version)
        </button>
      </section>

      {/* Forget device — danger */}
      <section className={`${CARD_CLS} p-4 space-y-3 border-red-900/40`}>
        <header className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-red-500/15 ring-1 ring-red-500/30 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-red-300" strokeWidth={2} />
          </div>
          <span className={LABEL_CLS}>Danger zone</span>
        </header>
        <p className="text-mono-300 text-sm">
          Forgetting this device wipes all cached data and unsynced drafts.
          You'll need to re-pair. Sync anything pending first.
        </p>
        <button
          type="button"
          className={BTN_DANGER_CLS}
          onClick={async () => {
            if (
              confirm(
                'Forget this device? Unsynced drafts will be lost. Re-pair required.',
              )
            ) {
              setSession(null);
              await wipeDb();
              navigate('/pair', { replace: true });
            }
          }}
        >
          Forget this device
        </button>
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number | string; tone: 'cyan' | 'red' | 'mono' }) {
  const cls = tone === 'cyan'
    ? 'text-cyan-300'
    : tone === 'red' ? 'text-red-300' : 'text-mono-200';
  return (
    <div className="rounded-md border border-mono-800 bg-mono-950/40 p-2 text-center">
      <div className={`text-base font-semibold ${cls}`}>{value}</div>
      <div className={`${LABEL_CLS} mt-0.5`}>{label}</div>
    </div>
  );
}
